import { Alert, Box } from '@factorialco/deprecated-design-system'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { budgetTypesEnum } from 'modules/project_management/lib/constants'

export interface IProjectBudgetValues {
  budgetType: budgetTypesEnum
  budgetAllocation: number | null | undefined
  budgetAllocationCents: number | null | undefined
  feeAmountCents: number | null | undefined
  currencyType: string
}

export interface IProjectBudgetFormProps {
  onChangeValue: (
    fieldName: string,
    fieldValue: number | string | undefined,
    shouldValidate?: boolean
  ) => void
  values: IProjectBudgetValues
  isOwner: boolean
  errors: FormErrors<IProjectBudgetValues>
  isBillable: boolean
  hasBudgetFeature: boolean
  hasCostsFeature: boolean
}

const ProjectBudgetForm = ({
  onChangeValue,
  values,
  errors,
  isOwner,
  isBillable,
  hasBudgetFeature,
  hasCostsFeature,
}: IProjectBudgetFormProps) => {
  const isFixedTimeBudgetSelected = values.budgetType === budgetTypesEnum.fixedTimeBudget
  const isFixedCostPlanningSelected = values.budgetType === budgetTypesEnum.fixedCostPlanning
  const canSeeCosts = isOwner

  const cost = values.budgetAllocationCents?.valueOf() || 0
  const income = values.feeAmountCents?.valueOf() || 0

  const isCostValidNumber = !isNaN(cost) && !(cost <= 0)
  const isIncomeValidNumber = !isNaN(income) && !(income <= 0)

  const validBenefit = isCostValidNumber && isIncomeValidNumber

  const monetaryProfit = income - cost
  const percentageProfit = (monetaryProfit / income) * 100

  const isPlanningDefined = values.budgetAllocationCents && values.feeAmountCents && validBenefit
  const currencyType = values.currencyType

  const hasFixedCostPlanning = hasBudgetFeature && hasCostsFeature

  const selectableOptions = [
    {
      label: i18n.t('project_management.planning.planning_options.without_planning'),
      value: budgetTypesEnum.withoutBudget,
    },
    {
      label: i18n.t('project_management.planning.planning_options.project_hours'),
      value: budgetTypesEnum.fixedTimeBudget,
    },
  ]
  if (canSeeCosts && hasFixedCostPlanning) {
    selectableOptions.push({
      label: i18n.t('project_management.planning.planning_options.project_cost'),
      value: budgetTypesEnum.fixedCostPlanning,
    })
  }

  const decimalLimitation = (value: string) => {
    const writingCommaOrDot = value.includes(',') || value.includes('.')

    const wholePart = value.split(/,|\./)[0]
    const decimalPart = value.split(/,|\./)[1]
    value = `${wholePart}`

    if (writingCommaOrDot) {
      value += `.${decimalPart?.slice(0, 2)}`
    }
    onChangeValue('budgetAllocation', Number(value))
  }

  return (
    <Box gap="s16">
      <Select
        label={i18n.t('project_management.planning.planning_type')}
        defaultValue={values.budgetType}
        options={selectableOptions}
        onChange={(value) => onChangeValue('budgetType', value)}
        readonly={!isOwner}
      />
      {isFixedTimeBudgetSelected && (
        <TextField
          label={i18n.t('project_management.planning.planned_hours')}
          mandatory
          type="number"
          value={values.budgetAllocation?.toString()}
          onChange={(value) => onChangeValue('budgetAllocation', value, true)}
          error={errors.budgetAllocation}
          onBlur={(value) =>
            value && values.budgetAllocation
              ? decimalLimitation(values.budgetAllocation.toString())
              : undefined
          }
        />
      )}
      {isFixedCostPlanningSelected && (
        <>
          <LabeledInput
            isLegend
            label={i18n.t('project_management.planning.planned_cost')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('project_management.planning.planned_cost_tooltip')}
              />
            }
          >
            <MoneyField
              currency={currencyType}
              placeholder="0"
              onChange={(value) => onChangeValue('budgetAllocationCents', value, true)}
              value={values.budgetAllocationCents?.toString()}
              error={errors.budgetAllocationCents}
              readonly={!isOwner}
              maxDecimals={2}
              mandatory
            />
          </LabeledInput>
          {isBillable && (
            <Box gap="s24">
              <LabeledInput
                isLegend
                label={i18n.t('project_management.planning.planned_income')}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'project_management.modals.create_project.wizard.step_three.income_alert'
                    )}
                  />
                }
              >
                <MoneyField
                  currency={currencyType}
                  placeholder="0"
                  onChange={(value) => onChangeValue('feeAmountCents', value, true)}
                  value={values.feeAmountCents?.toString()}
                  error={errors.feeAmountCents}
                  readonly={!isOwner}
                  maxDecimals={2}
                />
              </LabeledInput>
              {isPlanningDefined && (
                <Alert color="highlight">
                  {i18n.t('project_management.modals.create_project.wizard.step_three.alert', {
                    monetary_profit: monetaryProfit.toFixed(2),
                    percentage_profit: Math.round(percentageProfit),
                  })}
                </Alert>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default ProjectBudgetForm
